import { AppThunkAction } from "src/~store/models/AppThunkAction";
import {
    GetAllChatsAction,
    GetContactChatsLazyAction,
    SetSearchStringToChatsListAction,
    SetSearchStringToChatsInfoAction,
    SetFirstElementIdAction,
    SetSelectedMessageAction,
    ToggleMessageEditingAction,
    EditMessageAction,
    UpdateChatsAction,
    AddNewChatToStateAction,
    GetChatsModeChangedAction,
    ChangeChatContactChatnameAction,
    SearchChatsAction,
    GetChatTagsAction,
    GetChatTagValuesAction,
    ToggleAutoUpdateChatsAction,
    SearchChatsByChatIdAction,
    HideChatsAction
} from "./interfaces";
import {
    GET_ALL_CHATS_START,
    GET_ALL_CHATS_SUCCESS,
    GET_ALL_CHATS_ERROR,
    GET_CONTACT_CHATS_LAZY_START,
    GET_CONTACT_CHATS_LAZY_ERROR,
    GET_CONTACT_CHATS_LAZY_SUCCESS,
    SET_SEARCH_STRING_TO_CHATS_LIST_SUCCESS,
    SET_SEARCH_STRING_TO_CHATS_INFO_SUCCESS,
    SET_FIRST_ELEMENT_ID,
    SET_SELECTED_MESSAGE,
    TOGGLE_MESSAGE_EDITING,
    EDIT_MESSAGE_START,
    EDIT_MESSAGE_SUCCESS,
    EDIT_MESSAGE_ERROR,
    UPDATE_CHATS,
    ADD_NEW_CHAT_TO_CHATPANEL_STATE_SUCCESS,
    GET_CHATS_MODE_CHANGE_SUCCESS,
    GET_CHATS_MODE_CHANGE_START,
    CHANGE_CHAT_CONTACT_CHATNAME_SUCCESS,
    SEARCH_CHATS,
    GET_TAG_NAMES,
    GET_TAG_VALUES,
    TOGGLE_AUTO_UPDATE_CHATS,
    HIDE_CHATS,
} from "./types";
import {
    getChatsAsync,
    getMessagesByChatIdAsync,
    getContactChatsLazyAsync,
    getMessengerByIdAsync,
    editMessageAsync,
    getTagNames,
    getTagValuesByTagName,
    saveTagsAsync,
    saveChatCategoryAsync
} from "../../~api/actions";
import {
    getContactByIdAsync
} from "../../../contact/~api/actions";
import { IChat } from "../models/Chat";
import { IMessenger } from "src/app/contact/~store/models/Messenger";
import {
    showSuccessToastr,
    showErrorToastr,
} from "src/app/shared/helpers/toastr-helper/ToastrHelper";
import { IMessage } from "../models/Message";
import { GetChatsMode } from "../models/enums/GetChatsMode";
import { getDateTime } from "src/app/shared/helpers/date-helper/DateFormats";
import { IContact } from "src/app/contact/~store/models/Contact";
import { saveContactAsync } from "src/app/contact/~api/actions";
import { REQUESTED_NUMBER_OF_CHATS, SEARCHING_TAGS } from "../../chats-main/chats-list/ChatsListConstants";
import { ISearchingTag, TagType, TagName } from "src/app/shared/components/tag-searching-input/~types/models/SearchingTag";
import { IChatItem } from "src/app/chats-panel/~store/models/ChatItem";
import i18n from "src/app/shared/localization/i18n";
import { ITagModel } from "src/app/chat/specify-chat-close-reason-dialog/~store/models/TagModel";
import { GetChatMessagesAction } from "src/app/chats-panel/~store/actions/interfaces";
import { loadChatToWidget } from "src/app/chats-panel/~store/actions/actions";
import { redirectToLogin } from "src/~store/history";
import { ContactTagValue } from '../../../shared/helpers/contact-tag-value/ContactTagValue';

export const getChats =
    (
        skip: number,
        count: number,
        searchString: string,
        tags: ISearchingTag[],
        getChatsMode: GetChatsMode
    ): AppThunkAction<GetAllChatsAction> =>
    async (dispatch) => {
        dispatch({ type: GET_ALL_CHATS_START });

        const result = await getChatsAsync(
            skip,
            count,
            searchString,
            tags,
            getChatsMode
        );
        if (result.isSuccess) {
            const chats = result.value ?? [];
            dispatch({
                type: GET_ALL_CHATS_SUCCESS,
                payload: { chats, skipNumber: skip },
            });
        } else {
            dispatch({ type: GET_ALL_CHATS_ERROR });
            if (result.statusCode === 401) redirectToLogin();
        }
    };

export const getContactChatsLazy =
    (
        startChatId: number,
        beforeCount: number,
        afterCount: number,
        searchString: string,
        isLoadFromStart?: boolean
    ): AppThunkAction<GetContactChatsLazyAction> =>
    async (dispatch, getState) => {
        dispatch({ type: GET_CONTACT_CHATS_LAZY_START });
        const result = await getContactChatsLazyAsync(
            startChatId,
            beforeCount,
            afterCount,
            searchString
        );

        if (result.isSuccess) {
            const chats = result.value ?? [];
            for (const ch of chats) {
                const messagesResult = await getMessagesByChatIdAsync(
                    ch.chatId
                );
                ch.messages = messagesResult.isSuccess
                    ? messagesResult.value ?? []
                    : [];
                for (const msg of ch.messages) {
                    msg.timeSent = new Date(getDateTime(msg.timeSent));
                    msg.editTimestamp = msg.editTimestamp
                        ? new Date(getDateTime(msg.editTimestamp))
                        : undefined;
                }

                const getMessengerResult = await getMessengerByIdAsync(
                    ch.messengerId
                );
                ch.messenger = getMessengerResult.isSuccess
                    ? getMessengerResult.value ?? ({} as IMessenger)
                    : ({} as IMessenger);
            }
            dispatch({
                type: GET_CONTACT_CHATS_LAZY_SUCCESS,
                payload: {
                    contactChats: chats,
                    isBefore: beforeCount > 0 ? true : false,
                    isSetSearchString:
                        isLoadFromStart === undefined ||
                        isLoadFromStart === false
                            ? false
                            : true,
                },
            });
        } else {
            dispatch({ type: GET_CONTACT_CHATS_LAZY_ERROR });
            if (result.statusCode === 401) redirectToLogin();
        }
    };

export const setSearchStringToChatsList =
    (searchString: string): AppThunkAction<SetSearchStringToChatsListAction> =>
    async (dispatch, getState) => {
        dispatch({
            type: SET_SEARCH_STRING_TO_CHATS_LIST_SUCCESS,
            payload: { searchString },
        });
        getChats(
            0,
            REQUESTED_NUMBER_OF_CHATS,
            searchString,
            getState().chatsState.chatsList.tags,
            getState().chatsState.chatsList.getChatsMode
        )(dispatch, getState);
    };

export const searchChats =
    (tags: ISearchingTag[]): AppThunkAction<SearchChatsAction> =>
    async (dispatch, getState) => {        
        dispatch({ type: SEARCH_CHATS, payload: { tags } });
        getChats(
            0,
            REQUESTED_NUMBER_OF_CHATS,
            getState().contactState.contactList.searchString,
            tags,
            getState().chatsState.chatsList.getChatsMode
        )(dispatch, getState);
    };

export const setSearchStringToChatsInfo =
    (
        searchString: string,
        chatId: number
    ): AppThunkAction<SetSearchStringToChatsInfoAction> =>
    async (dispatch, getState) => {
        dispatch({
            type: SET_SEARCH_STRING_TO_CHATS_INFO_SUCCESS,
            payload: { searchString },
        });

        const countOfChats =
            searchString !== ""
                ? getState().chatsState.contactChatsInfo.contactChats.length
                : 1;
        const startChatId =
            getState().chatsState.contactChatsInfo.contactChats[0] !== undefined
                ? getState().chatsState.contactChatsInfo.contactChats[0].chatId
                : chatId;
        getContactChatsLazy(
            startChatId,
            0,
            countOfChats,
            searchString,
            true
        )(dispatch, getState);
    };

export const setFirstElementId =
    (id: string): AppThunkAction<SetFirstElementIdAction> =>
    async (dispatch) => {
        dispatch({ type: SET_FIRST_ELEMENT_ID, payload: { id } });
    };

export const setSelectedMessage =
    (message: IMessage | undefined): AppThunkAction<SetSelectedMessageAction> =>
    async (dispatch) => {
        dispatch({ type: SET_SELECTED_MESSAGE, payload: { message } });
    };

export const toggleMessageEditing =
    (): AppThunkAction<ToggleMessageEditingAction> => async (dispatch) => {
        dispatch({ type: TOGGLE_MESSAGE_EDITING });
    };

export const editMessage =
    (editedMessage: IMessage): AppThunkAction<EditMessageAction> =>
    async (dispatch, getState) => {
        dispatch({ type: EDIT_MESSAGE_START });
        const result = await editMessageAsync(
            editedMessage.messageId,
            editedMessage.text
        );

        if (result.isSuccess) {
            dispatch({ type: EDIT_MESSAGE_SUCCESS });
            showSuccessToastr(i18n.t("chats.editMessageSuccessMessage"));
        } else {
            dispatch({ type: EDIT_MESSAGE_ERROR });
            showErrorToastr(i18n.t("chats.editMessageErrorMessage"));
        }

        getContactChatsLazy(
            getState().chatsState.contactChatsInfo.contactChats[0].chatId,
            0,
            getState().chatsState.contactChatsInfo.contactChats.length,
            getState().chatsState.contactChatsInfo.searchString,
            true
        )(dispatch, getState);
    };

export const onChatItemClick =
    (chatId: number): AppThunkAction<GetChatMessagesAction> =>
    async (dispatch, getState) => {
        loadChatToWidget(chatId)(dispatch, getState);
    };

export const updateChats =
    (chats: IChat[]): AppThunkAction<UpdateChatsAction> =>
    async (dispatch, getState) => {
        dispatch({
            type: UPDATE_CHATS,
            payload: {
                chats,
                operatorId: getState().chatItemsState.operatorId,
                permissions:
                    getState().userContextState.permissions.permissionList,
            },
        });
    };

export const hideChats =
    (chatsIds: number[]): AppThunkAction<HideChatsAction> =>
    async (dispatch) => {
        dispatch({
            type: HIDE_CHATS,
            payload: {
                chatsIds,
            },
        });
    };

export const addNewChatToState =
    (chat: IChat): AppThunkAction<AddNewChatToStateAction> =>
    async (dispatch, getState) => {
        const operatorId = getState().chatItemsState.operatorId;
        if (
            getState().chatsState.chatsList.getChatsMode === GetChatsMode.my &&
            operatorId !== chat.operatorId
        )
            return;
        if (
            getState().chatsState.chatsList.getChatsMode ===
                GetChatsMode.withMe &&
            chat.invitedOperatorsId.indexOf(operatorId) === -1
        )
            return;
        if (
            getState().chatsState.chatsList.getChatsMode ===
            GetChatsMode.archive
        )
            return;

        dispatch({
            type: ADD_NEW_CHAT_TO_CHATPANEL_STATE_SUCCESS,
            payload: { chat },
        });
    };

export const onGetChatsModeChanged =
    (
        mode: GetChatsMode,
        needGetChats: boolean = false
    ): AppThunkAction<GetChatsModeChangedAction> =>
    async (dispatch, getState) => {
        dispatch({ type: GET_CHATS_MODE_CHANGE_START });
        if (needGetChats) {
            getChats(
                0,
                REQUESTED_NUMBER_OF_CHATS,
                "",
                [],
                mode
            )(dispatch, getState);
        }
        dispatch({ type: GET_CHATS_MODE_CHANGE_SUCCESS, payload: { mode } });
    };

export const changeChatContactChatName =
    (
        contact: IContact,
        chatId: number
    ): AppThunkAction<ChangeChatContactChatnameAction> =>
    async (dispatch) => {
        const result = await saveContactAsync(contact);

        if (result.isSuccess) {
            dispatch({
                type: CHANGE_CHAT_CONTACT_CHATNAME_SUCCESS,
                payload: { contact, chatId },
            });
        }
    };

export const selectChat =
    (
        isNext: boolean,
        chats: IChat[],
        selectedChatItem: IChatItem | undefined
    ): AppThunkAction<GetChatMessagesAction> =>
    async (dispatch, getState) => {
        if (selectedChatItem === undefined) {
            return;
        }

        const selectedChat = chats.find(
            (chat) => chat.chatId === selectedChatItem.chatId
        );
        if (selectedChat === undefined) {
            const firstChat = chats[0];
            if (firstChat !== undefined) {
                onChatItemClick(firstChat.chatId)(dispatch, getState);
            } else {
                return;
            }
        } else {
            const selectedChatIndex = chats.indexOf(selectedChat);
            var nextChatIndex = isNext
                ? selectedChatIndex + 1
                : selectedChatIndex - 1;

            if (nextChatIndex < 0) {
                nextChatIndex = chats.length - 1;
            }
            if (nextChatIndex >= chats.length) {
                nextChatIndex = 0;
            }

            const nextChat = chats[nextChatIndex];
            if (nextChat === undefined) {
                const firstChat = chats[0];
                if (firstChat !== undefined) {
                    onChatItemClick(firstChat.chatId)(dispatch, getState);
                }
                return;
            }

            onChatItemClick(nextChat.chatId)(dispatch, getState);
        }
    };

export const getExistsTagNames =
    (): AppThunkAction<GetChatTagsAction> => async (dispatch) => {
        const result = await getTagNames();

        if (result.isSuccess) {
            const chatTags = result.value ?? [];
            dispatch({ type: GET_TAG_NAMES, payload: { chatTags } });
        }
    };

export const getExistsTagValuesByTagName =
    (name: string): AppThunkAction<GetChatTagValuesAction> =>
    async (dispatch) => {
        const result = await getTagValuesByTagName(name);

        if (result.isSuccess) {
            const chatTagValues = result.value ?? [];
            dispatch({ type: GET_TAG_VALUES, payload: { chatTagValues } });
        }
    };

export const saveTags =
    (tags: ITagModel[]): AppThunkAction<void> =>
    async () => {
        await saveTagsAsync(tags);
        };

export const saveChatCategory =
    (chatId: number, chatCategory: string): AppThunkAction<void> =>
        async () => {
            await saveChatCategoryAsync(chatId, chatCategory);
        };

export const toggleAutoUpdateChats =
    (): AppThunkAction<ToggleAutoUpdateChatsAction> => async (dispatch) => {
        dispatch({ type: TOGGLE_AUTO_UPDATE_CHATS });
    };

export const searchChatsByContactId = (contactId: number): AppThunkAction<SearchChatsByChatIdAction> => async (dispatch, getState) => {
    const result = await getContactByIdAsync(contactId);    
    if (result.isSuccess && result.value) {
        const contact = result.value as IContact;
        const openingContactTag = {...SEARCHING_TAGS.find(tag => tag.tagType === TagType.OpeningTag && tag.tagName === TagName.Contact)} as ISearchingTag;
        const operationTag = {...SEARCHING_TAGS.find(tag => tag.tagType === TagType.OperationTag && tag.value === "=")} as ISearchingTag;
        const closingTag = {...SEARCHING_TAGS.find(tag => tag.tagType === TagType.ClosingTag && tag.tagName === TagName.Contact), value: ContactTagValue(contact), externalValue: contact.contactId.toString()} as ISearchingTag;
        const tags = getState().chatsState.chatsList.tags.concat(openingContactTag, operationTag, closingTag);
        await searchChats(tags)(dispatch, getState);
    }
}
